"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatasourceFieldsDisplayGrid = exports.noContentSearchBase = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const catalog_queries_1 = require("@tableau/catalog-queries");
const data_catalog_shared_1 = require("@tableau/data-catalog-shared");
const SpotFilterBaseIcon_1 = require("@tableau/icons/SpotFilterBaseIcon");
const widgets_1 = require("@tableau/widgets");
const react_1 = tslib_1.__importDefault(require("react"));
const NumberCellFormatter_1 = require("../datagrid/cellFormatters/NumberCellFormatter");
const QueryPreservingLinkCellFormatter_1 = require("../datagrid/cellFormatters/QueryPreservingLinkCellFormatter");
const SvgFieldIcon_1 = require("../field/components/SvgFieldIcon");
const FieldNameMapping_1 = require("../field/functions/FieldNameMapping");
const DescriptionInfo_1 = require("../fieldColumn/DescriptionInfo");
const LabelsHookHelper_1 = require("../Labels/LabelsHookHelper");
const FieldsDisplayGrid_1 = require("../sharedDisplayGrid/FieldsDisplayGrid");
function columns(siteUrls) {
    const columns = [
        () => (0, data_catalog_shared_1.createSelectionColumn)(),
        (sortParams) => ({
            name: 'type',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderType(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, data_catalog_shared_1.createIconCellFormatter)(({ fieldIcon }) => react_1.default.createElement(SvgFieldIcon_1.SvgFieldIcon, { fieldIcon: fieldIcon }), ({ fieldIcon }) => fieldIcon ? FieldNameMapping_1.FieldNameMappings[fieldIcon]() : ''),
            sortable: false,
            resizable: false,
        }),
        () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
        (sortParams) => ({
            name: 'name',
            dataKey: catalog_queries_1.FieldOrderField.NAME,
            headerText: catalog_messages_1.Messages.GridHeaderName(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: (0, QueryPreservingLinkCellFormatter_1.createQueryPreservingReplacingLinkCellFormatter)(FieldsDisplayGrid_1.getNameWithApplicableDataType, (urls, { id, datasourceId }) => urls.datasourceLineageFieldPath(datasourceId, id)),
            sortable: true,
        }),
        (sortParams) => ({
            name: 'sheets',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderSheets(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams, 'right'),
            cellFormatter: (0, NumberCellFormatter_1.createNumberCellFormatter)(({ sheetCount }) => sheetCount),
            sortable: false,
        }),
        (sortParams) => ({
            name: 'description',
            dataKey: data_catalog_shared_1.UnsortableColumnKey,
            headerText: catalog_messages_1.Messages.GridHeaderDescription(),
            headerFormatter: (0, data_catalog_shared_1.createSortableHeaderFormatter)(sortParams),
            cellFormatter: descriptionCellFormatter(),
            sortable: false,
            growToFit: true,
        }),
        () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
    ];
    return columns.filter(Boolean);
}
exports.noContentSearchBase = (0, data_catalog_shared_1.cssClass)('noContentSearchBase', {
    display: 'flex',
    flexFlow: 'column',
    overflow: 'auto',
    alignSelf: 'center',
    alignItems: 'center',
    flexGrow: 'inherit',
    justifyContent: 'center',
    fontWeight: 'normal',
});
function DatasourceFieldsDisplayGrid(props) {
    const { customSensitivityLabelsEnabled, searchAndFilterOnLineagePageEnabled } = (0, catalog_core_1.useFeatureFlags)();
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    const cols = columns(siteUrls)
        .map(columnFactory => columnFactory(props))
        .filter(column => {
        if (column.name === 'label-sensitivity' && !customSensitivityLabelsEnabled) {
            return false;
        }
        return true;
    });
    if (props.rowCount === 0 && searchAndFilterOnLineagePageEnabled) {
        return (react_1.default.createElement("div", { className: (0, data_catalog_shared_1.classes)(exports.noContentSearchBase) },
            react_1.default.createElement(SpotFilterBaseIcon_1.SpotFilterBaseIcon, { size: 80, alt: catalog_messages_1.Messages.FilterNoContent() }),
            react_1.default.createElement("div", null, catalog_messages_1.Messages.FilterNoContent()),
            react_1.default.createElement(widgets_1.ButtonWidget, { testId: 'lineage-reset-filter', onClick: props.resetFilter ? props.resetFilter : () => {
                    return;
                } },
                react_1.default.createElement("span", null, catalog_messages_1.Messages.ResetFilter()))));
    }
    return (react_1.default.createElement(data_catalog_shared_1.ExplorerStyledDisplayGrid, { columns: cols, rowStore: props.rowStore, selectedKeys: props.selectedKeys, updateSelectionForKey: props.updateSelectionForKey, rowCount: props.rowCount, renderErrors: data_catalog_shared_1.reportGraphQLErrors, orderBy: props.orderBy, updateSortOrder: props.updateSortOrder }));
}
exports.DatasourceFieldsDisplayGrid = DatasourceFieldsDisplayGrid;
// Returns a cell formatter that displays a description and, if applicable, a description with inheritance info
function descriptionCellFormatter() {
    return (0, data_catalog_shared_1.createTextCellFormatter)(descriptionTextFromRowData, // getTextFromRowData
    catalog_messages_1.Messages.NoDescriptionGridPlaceholder(), // placeholder
    () => false, // getDisabledFromRowData (always enabled)
    false, // noPadding
    descriptionIconFromRowData);
}
// Call the helper methods with only the needed fields. I can't call the helper methods
// with a broader row-data type because createTextCellFormatter mandates that the interfaces
// of the helper methods accept the same broad type.
function minifyRowData(rowData) {
    return {
        description: rowData.description,
        descriptionInherited: rowData.descriptionInherited,
    };
}
function descriptionTextFromRowData(rowData) {
    return (0, DescriptionInfo_1.DescriptionTextFromRowData)(minifyRowData(rowData));
}
function descriptionIconFromRowData(rowData) {
    return (0, DescriptionInfo_1.DescriptionIconFromRowData)(minifyRowData(rowData));
}
function getLabelContentFromRowData(rowData) {
    var _a;
    const contentParams = {
        contentId: rowData.id,
        contentType: null,
        contentName: (_a = rowData.name) !== null && _a !== void 0 ? _a : undefined,
        subjectArea: 'Field',
    };
    return contentParams;
}
function createLabelGridColumnFormatter(urls, category) {
    function getPropsFromRowData(rowData) {
        const content = getLabelContentFromRowData(rowData);
        const labels = rowData.labels ? rowData.labels : [];
        return (0, LabelsHookHelper_1.getLabelParamsForGrids)({ category, labels, urls, source: 'datasource-fields', content });
    }
    return (0, data_catalog_shared_1.createLabelColumnFormatter)((0, LabelsHookHelper_1.labelCategoryToString)(category), {
        overrides: {},
        getPropsFromRowData,
    });
}
//# sourceMappingURL=DatasourceFieldsDisplayGrid.js.map